import { useCallback, useEffect } from 'react'

import {
  OfChatMessagesService,
  type OnlyFansChatDto,
  type OnlyFansMessage,
  TeamMemberRole,
} from '@api'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $account } from '@/models/account.ts'
import {
  clearChatSuggestionsByVersion,
  setChatSuggestions,
} from '@/models/chattingUI/chatSuggestions/chatSuggestions'
import {
  $allowedFanNumbering,
  $chatFilter,
  $chatOrder,
  $chatQuery,
  $chats,
  $fanId,
  $selectedList,
  addChat,
  resetChats,
  updateLastMessage,
} from '@/models/chattingUI/chats/chats.ts'
import { startFanTyping } from '@/models/chattingUI/chats/typing.ts'
import {
  addMessageByFanId,
  resetMessagesByFanId,
  sendMessageFx,
} from '@/models/chattingUI/messages/messages.ts'
import { $creatorId } from '@/models/creators.ts'
import {
  $allowedFanNumbers,
  $inboxSplitterStatus,
  finishFanNumberingUpdating,
  startFanNumberingUpdating,
} from '@/modules/automations/models/fan-numbering'
import type {
  MessageQueue,
  OnlineCountConfig,
} from '@/modules/chatting/types.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { usePusher } from '@/modules/pusher/PusherProvider.js'
import { PusherEvents } from '@/modules/pusher/constants.ts'
import { matchesConditions } from '@/utils/chats.ts'

import { PusherToast } from '../ui/components/pusher-toast'
import { useMessageQueue } from './use-message-queue'

interface UsePusherListenerParams {
  handleOnlineCountUpdate: (config: OnlineCountConfig) => void
}

export const usePusherListener = ({
  handleOnlineCountUpdate,
}: UsePusherListenerParams) => {
  const creatorId = useUnit($creatorId)
  const account = useUnit($account)
  const inboxSplitterStatus = useUnit($inboxSplitterStatus)
  const allowedFanNumbers = useUnit($allowedFanNumbers)
  const fanId = useUnit($fanId)
  const selectedList = useUnit($selectedList)
  const query = useUnit($chatQuery)
  const filter = useUnit($chatFilter)
  const order = useUnit($chatOrder)
  const queryClient = useQueryClient()
  const { queryKey } = useMessageQueue()
  const toast = useToast()
  const { subscribeToChannel } = usePusher()
  const allowedFanNumbering = useUnit($allowedFanNumbering)
  const { setSettingsData } = useSettingsContext()
  const handleMessageUpdate = useCallback(
    ({
      chat,
      message,
      isLastMessage,
    }: {
      chat: OnlyFansChatDto
      message: OnlyFansMessage
      isLastMessage: boolean
    }) => {
      const userId = chat.withUser.id

      // restrict by fan numbering until DEV-5533 are done
      if (
        allowedFanNumbering.length &&
        !allowedFanNumbering.includes(chat.withUser.fanNumber!)
      ) {
        return
      }

      // update chat preview in a chat list
      if (isLastMessage) {
        // eslint-disable-next-line effector/no-getState
        const chats = $chats.getState()
        const isMatchingConditions = !!matchesConditions(
          filter,
          query,
          selectedList,
          chat,
        )

        if (
          !chats.some(({ id }) => id === chat.withUser.id) &&
          isMatchingConditions
        ) {
          addChat(chat)
        }

        updateLastMessage({
          fanId: userId,
          lastMessage: message,
          pushToTheTopIfChanged:
            order === 'recent' && isMatchingConditions && !message.isFromQueue,
        })
      } else {
        addMessageByFanId({
          fanId: userId,
          message,
        })
      }
    },
    [allowedFanNumbering, filter, order, query, selectedList],
  )

  useEffect(() => {
    if (!creatorId) {
      return
    }

    return subscribeToChannel(
      `private-chatting-ui-${creatorId}`,
      (event, data) => {
        console.log('Chatting UI Pusher event', event, data)

        if (event === PusherEvents.FAN_TYPING) {
          startFanTyping(data.id)
        }

        if (event === PusherEvents.AICH_SUGGESTION_GENERATED) {
          setChatSuggestions(data)
        }

        if (event === PusherEvents.AICH_SUGGESTION_ACCEPTED) {
          clearChatSuggestionsByVersion(data)
        }

        if (!creatorId || !account) {
          return
        }

        if (event === PusherEvents.UPDATED_MESSAGE) {
          // didn't use as hook dependency because it's not needed to re-render the component when the message is updated'
          const isSending = sendMessageFx.pending.getState()

          // We need to skip own message update since it already happens in sendMessageFx and leads to message flickering, except when your are not sending a message
          if (
            data.chat?.withUser.id === fanId &&
            data.message?.fromUser.id !== fanId &&
            isSending
          ) {
            return
          }

          if (data.chat && data.message) {
            handleMessageUpdate({
              chat: data.chat,
              message: data.message,
              isLastMessage: data.isLastMessage,
            })
          } else if (data.userId && data.messageId) {
            OfChatMessagesService.getDetailedChatMessage({
              creatorId,
              userId: data.userId,
              messageId: data.messageId,
            }).then(({ chat, message }) => {
              handleMessageUpdate({
                chat: chat!,
                message: message!,
                isLastMessage: data.isLastMessage,
              })
            })
          }
        }

        if (event === PusherEvents.TOAST) {
          if (data.type === 'success') {
            toast.success(data.text, {
              as: PusherToast,
            })
          } else if (data.type === 'error') {
            toast.error(data.text, {
              as: PusherToast,
            })
          }
        }

        if (event === PusherEvents.ONLINE_COUNT_UPDATE) {
          if (
            !inboxSplitterStatus?.inboxSplitterEnabled ||
            account.memberRole !== TeamMemberRole.MEMBER
          ) {
            handleOnlineCountUpdate({
              count: data.totalCount,
              updatedAt: Date.now(),
            })
          }

          if (
            account.memberRole === TeamMemberRole.MEMBER &&
            inboxSplitterStatus?.inboxSplitterEnabled
          ) {
            const userIdStr = String(account.userId)
            const accessibleNumbers: number[] = []

            if (allowedFanNumbers?.userIdsToTeamMember) {
              Object.entries(allowedFanNumbers.userIdsToTeamMember).forEach(
                ([fanNumber, teamMembers]) => {
                  const hasAccess = teamMembers.some(
                    (member) => String(member.userId) === userIdStr,
                  )

                  if (hasAccess) {
                    accessibleNumbers.push(Number(fanNumber))
                  }
                },
              )
            }

            const totalCount = data.countPerNumber.reduce((sum, item) => {
              if (item.number && accessibleNumbers.includes(item.number)) {
                return sum + (item.count || 0)
              }

              return sum
            }, 0)

            handleOnlineCountUpdate({
              count: totalCount,
              updatedAt: Date.now(),
            })
          }
        }

        if (event === PusherEvents.NOTIFICATION_COUNTS) {
          queryClient.setQueryData(
            ['unreadNotificationsCount', creatorId],
            data,
          )
        }

        if (event === PusherEvents.UPDATED_QUEUE) {
          const currentQueueList =
            queryClient.getQueryData<MessageQueue[]>(queryKey)

          const isInQueue = currentQueueList?.find(
            (queue) => queue.id === data.queueEntry.id,
          )

          if (isInQueue) {
            const newQueueList =
              currentQueueList?.map((queue) =>
                queue.id === data.queueEntry.id ? data.queueEntry : queue,
              ) || []

            queryClient.setQueryData<MessageQueue[]>(queryKey, newQueueList)
          } else {
            queryClient.setQueryData<MessageQueue[]>(queryKey, [
              ...(currentQueueList || []),
              data.queueEntry,
            ])
          }

          if (data.queueEntry.isDone) {
            resetChats()
            if (fanId) {
              resetMessagesByFanId({ fanId })
            }
          }
        }

        if (event === PusherEvents.FAN_NUMBERING_STARTED_PROCESSING) {
          startFanNumberingUpdating()
        }

        if (event === PusherEvents.FAN_NUMBERING_FINISHED_PROCESSING) {
          finishFanNumberingUpdating()
          resetChats()
        }
      },
    )
  }, [
    fanId,
    creatorId,
    handleMessageUpdate,
    handleOnlineCountUpdate,
    queryClient,
    queryKey,
    subscribeToChannel,
    toast,
    setSettingsData,
    account,
    inboxSplitterStatus,
    allowedFanNumbers,
  ])
}
